/**
 * VaultSidebar — shared left-rail for the DATA CENTER tabs.
 *
 * A single-select tree for every tab ('catalog' / 'query'): click a table
 * to drive the right-hand panel (table details / SELECT * snippet).
 *
 * Every mode shares:
 *   - Medallion-layer Segmented switcher (bronze / staging / silver / gold)
 *   - Search box
 *   - Schema-grouped tree built from a single getVaultErd() call held in
 *     VaultDataContext.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Tree, Input, Space, Typography, Button, Empty, Spin,
  Tooltip, Dropdown, message, Checkbox,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, DatabaseOutlined, TableOutlined,
  MoreOutlined, DeleteOutlined, KeyOutlined, CopyOutlined,
  BuildOutlined, GoldOutlined, InboxOutlined, CloudServerOutlined,
} from '@ant-design/icons';
import { useVaultData } from '../contexts/VaultDataContext';
import ResetVaultDialog from './ResetVaultDialog';

// Per-layer presentation: accent colour (mirrors the App.css
// `.vault-layer-chip-<layer>` palette) + an iconic glyph. Used to render
// the layer-group header rows in the schema tree.
const LAYER_META = {
  staging: { label: 'Staging', color: '#1677ff', Icon: CloudServerOutlined },
  bronze:  { label: 'Bronze',  color: '#c08457', Icon: BuildOutlined },
  silver:  { label: 'Silver',  color: '#8c8c8c', Icon: DatabaseOutlined },
  gold:    { label: 'Gold',    color: '#c9a961', Icon: GoldOutlined },
  legacy:  { label: 'Legacy',  color: '#722ed1', Icon: InboxOutlined },
};
const LAYER_ORDER = ['staging', 'bronze', 'silver', 'gold', 'legacy'];

// Copy `text` to the clipboard and show a tiny success toast. Used by
// the inline Copy buttons that the Vault sidebar shows next to each
// table / column row while the SQL Workbench tab is active — gives the
// user a one-click way to compose `FROM "schema"."table"` or paste a
// column name into the editor without typing it.
async function copyToClipboard(text, label) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      // Legacy fallback for non-secure contexts.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    message.success(`Copied ${label}`);
  } catch (err) {
    message.error(`Couldn't copy: ${err.message}`);
  }
}

function CopyChip({ text, label, title }) {
  return (
    <Tooltip title={title} mouseEnterDelay={0.4}>
      <button
        type="button"
        className="vault-tree-copy-chip"
        onClick={(e) => {
          // Stop the click from selecting / expanding the tree node.
          e.preventDefault();
          e.stopPropagation();
          copyToClipboard(text, label);
        }}
        // Antd Tree calls onSelect from a synthetic click; mousedown
        // already triggers selection, so we also swallow that here.
        onMouseDown={(e) => e.stopPropagation()}
        aria-label={`Copy ${label}`}
      >
        <CopyOutlined />
      </button>
    </Tooltip>
  );
}

const { Text } = Typography;

// ── Pure helpers extracted from VaultSidebar ──────────────────────────
// `resolveVaultSidebarContext` collapses the ~16 `v?.field || fallback`
// reads that used to live directly in the component body (each one adds
// a branch to VaultSidebar's own cyclomatic complexity) into a single
// call. The `build*TreeNode` family below replaces the nested
// map-within-map-within-map that built `treeData` — each is now its own
// top-level function (not a function nested inside another function's
// body), so no closure sits more than one level deep. Pure relocation:
// same key formats, same sort/filter rules, same fallback values as
// before. None of this touches ResetVaultDialog's reset/rotation flow —
// this component only opens that dialog.

function vaultSidebarDataDefaults(v) {
  return {
    layerSchemas: v?.layerSchemas || [],
    tablesBySchema: v?.tablesBySchema || {},
    tablesLoading: v?.tablesLoading || {},
    loadSchemaTables: v?.loadSchemaTables || (async () => []),
    layerCounts: v?.layerCounts || new Map(),
    loading: v?.loading ?? false,
    refresh: v?.refresh || (() => {}),
    layers: v?.layers || ['bronze'],
  };
}

function vaultSidebarUiDefaults(v) {
  return {
    toggleLayer: v?.toggleLayer || (() => {}),
    showConnectedOnly: v?.showConnectedOnly || false,
    setShowConnectedOnly: v?.setShowConnectedOnly || (() => {}),
    connectedConnectors: v?.connectedConnectors || new Set(),
    selectedSchema: v?.selectedSchema || '',
    selectedTable: v?.selectedTable || '',
    selectTable: v?.selectTable || (() => {}),
    erd: v?.erd,
    erdLoading: v?.erdLoading,
    ensureErd: v?.ensureErd || (() => {}),
  };
}

export function resolveVaultSidebarContext(v) {
  return { ...vaultSidebarDataDefaults(v), ...vaultSidebarUiDefaults(v) };
}

export function resolveSelectedTreeKey({ selectedSchema, selectedTable }) {
  return selectedTable ? [`t::${selectedSchema}::${selectedTable}`] : [];
}

export function buildLayerOptions(layerCounts) {
  return [
    { value: 'staging', label: 'Staging', count: layerCounts.get('staging') || 0 },
    { value: 'bronze', label: 'Bronze', count: layerCounts.get('bronze') || 0 },
    { value: 'silver', label: 'Silver', count: layerCounts.get('silver') || 0 },
    { value: 'gold', label: 'Gold', count: layerCounts.get('gold') || 0 },
    ...(layerCounts.get('legacy') ? [{ value: 'legacy', label: 'Legacy', count: layerCounts.get('legacy') }] : []),
  ];
}

export function resolveSchemaTreePanelKind({ loading, treeDataLength }) {
  if (loading) {return 'loading';}
  if (treeDataLength === 0) {return 'empty';}
  return 'ready';
}

export function resolveEmptySchemaMessage(layerSchemasLength) {
  return layerSchemasLength === 0 ? 'No tables in your vault yet' : 'No matches';
}

export function resolveSchemaSearchPlaceholder(tab) {
  return tab === 'query' ? 'Search tables (double-click to insert)…' : 'Search tables…';
}

export function resolveTreeDoubleClickHandler({ tab, onDoubleClick }) {
  return tab === 'query' ? onDoubleClick : undefined;
}

function loadingColumnLabel(erdReady, erdLoading) {
  if (erdReady) {return 'No columns';}
  return erdLoading ? 'Loading columns…' : 'Click to load columns…';
}

export function buildLoadingColumnPlaceholder(tableId, erdReady, erdLoading) {
  return {
    key: `c::${tableId}::__loading`,
    title: (
      <Space size={6}>
        {erdLoading ? <Spin size="small" /> : null}
        <Text type="secondary" style={{ fontSize: 11 }}>
          {loadingColumnLabel(erdReady, erdLoading)}
        </Text>
      </Space>
    ),
    isLeaf: true,
    selectable: false,
  };
}

export function buildColumnTreeNode(c, idx, { tableId, showCopy }) {
  return {
    key: `c::${tableId}::${c.name || idx}`,
    title: (
      <span className="vault-tree-row vault-tree-row-column">
        {c.is_primary_key
          ? <KeyOutlined className="vault-tree-row-icon vault-tree-row-icon-pk" />
          : <span className="vault-tree-row-icon vault-tree-row-icon-col-bullet" />}
        <span className="vault-tree-row-label">{c.name}</span>
        {c.type && (
          <span className="vault-tree-row-coltype">{c.type}</span>
        )}
        {showCopy && c.name && (
          <CopyChip
            text={c.name}
            label={`column ${c.name}`}
            title={`Copy column "${c.name}"`}
          />
        )}
      </span>
    ),
    isLeaf: true,
    selectable: false,
    _column: c,
  };
}

export function buildTableTreeNode(t, s, { columnsByTable, erd, erdLoading, showCopy }) {
  const tableId = `${t.schema_name}.${t.table}`;
  const cols = columnsByTable.get(tableId);
  const erdReady = erd != null;
  const tableChildren = cols
    ? cols.map((c, idx) => buildColumnTreeNode(c, idx, { tableId, showCopy }))
    : [buildLoadingColumnPlaceholder(tableId, erdReady, erdLoading)];

  const qualified = `"${t.schema_name}"."${t.table}"`;
  return {
    key: `t::${s.connector}::${t.table}`,
    title: (
      <span className="vault-tree-row vault-tree-row-table">
        <TableOutlined className="vault-tree-row-icon vault-tree-row-icon-table" />
        <span className="vault-tree-row-label">{t.table}</span>
        {showCopy && (
          <CopyChip
            text={qualified}
            label={`table ${t.table}`}
            title={`Copy ${qualified}`}
          />
        )}
      </span>
    ),
    isLeaf: false,
    _table: t,
    _tableId: tableId,
    children: tableChildren,
  };
}

// Same-cron table list used for both the tree filter (search) and the
// sort — always sorted, only filtered when there's an active query.
export function sortTablesForSchema(cached, q, connectorLower) {
  const matched = q
    ? cached.filter((t) => t.table.toLowerCase().includes(q) || connectorLower.includes(q))
    : cached;
  return matched.slice().sort((a, b) => a.table.localeCompare(b.table));
}

export function buildSchemaChildren(s, cached, { tablesLoading, search, columnsByTable, erd, erdLoading, tab }) {
  const isLoading = !!tablesLoading[s.schema_name];
  if (isLoading) {
    return [{
      key: `s::${s.connector}::__loading`,
      title: <Space size={6}><Spin size="small" /><Text type="secondary">Loading…</Text></Space>,
      isLeaf: true,
      selectable: false,
    }];
  }
  if (!cached) {
    // Placeholder so the schema node shows an expander even before its
    // tables are loaded. Antd Tree only renders an expand caret when the
    // node has at least one child.
    return [{
      key: `s::${s.connector}::__placeholder`,
      title: <Text type="secondary" style={{ fontSize: 11 }}>Click to load…</Text>,
      isLeaf: true,
      selectable: false,
    }];
  }
  const q = search.trim().toLowerCase();
  const tablesForSchema = sortTablesForSchema(cached, q, s.connector.toLowerCase());
  const showCopy = tab === 'query';
  return tablesForSchema.map((t) => buildTableTreeNode(t, s, { columnsByTable, erd, erdLoading, showCopy }));
}

export function buildSchemaTreeNode(s, ctx) {
  const cached = ctx.tablesBySchema[s.schema_name];
  const children = buildSchemaChildren(s, cached, ctx);
  return {
    key: `s::${s.connector}`,
    title: (
      <span className="vault-tree-row vault-tree-row-schema">
        <DatabaseOutlined className="vault-tree-row-icon vault-tree-row-icon-schema" />
        <span className="vault-tree-row-label vault-tree-row-label-strong">{s.connector}</span>
        <span className="vault-tree-row-count">
          {cached ? children.filter((c) => !String(c.key).endsWith('__placeholder') && !String(c.key).endsWith('__loading')).length : s.table_count}
        </span>
      </span>
    ),
    selectable: false,
    _schemaName: s.schema_name,
    _layer: s.layer,
    children,
  };
}

// Group schema nodes under per-layer header rows (iconic colour + icon).
export function groupSchemaNodesByLayer(schemaNodes) {
  const byLayer = new Map();
  for (const node of schemaNodes) {
    const lyr = node._layer || 'legacy';
    if (!byLayer.has(lyr)) {byLayer.set(lyr, []);}
    byLayer.get(lyr).push(node);
  }
  return LAYER_ORDER.filter((lyr) => byLayer.has(lyr)).map((lyr) => {
    const meta = LAYER_META[lyr] || LAYER_META.legacy;
    const GroupIcon = meta.Icon;
    const kids = byLayer.get(lyr);
    return {
      key: `l::${lyr}`,
      className: `vault-layer-group vault-layer-group-${lyr}`,
      selectable: false,
      title: (
        <span className="vault-layer-group-row" style={{ '--layer-accent': meta.color }}>
          <GroupIcon className="vault-layer-group-icon" />
          <span className="vault-layer-group-label">{meta.label}</span>
          <span className="vault-layer-group-count">{kids.length}</span>
        </span>
      ),
      _layerGroup: lyr,
      children: kids,
    };
  });
}

export function buildVaultTreeData(filteredSchemas, ctx) {
  const schemaNodes = filteredSchemas.map((s) => buildSchemaTreeNode(s, ctx));
  return groupSchemaNodesByLayer(schemaNodes);
}

export default function VaultSidebar({
  tab,                  // 'catalog' | 'query'
  width = 320,
  onDoubleClickTable,   // optional — used by Query tab for "insert SELECT *"
}) {
  const v = useVaultData();
  const [search, setSearch] = useState('');
  // Reset-data dialog. Lived in DataExplorerPage's standalone Sider
  // before DATA CENTER moved to a shared sidebar — restored here so
  // the destructive-reset flow is reachable again from /data/center.
  const [resetOpen, setResetOpen] = useState(false);

  // Read with safe fallbacks so the hooks below run unconditionally even
  // when this component is rendered outside a VaultDataProvider (e.g.
  // tests). The actual `null` short-circuit lives at the bottom of the
  // hook list, after every hook call has been issued.
  const {
    layerSchemas, tablesBySchema, tablesLoading, loadSchemaTables, layerCounts,
    loading, refresh, layers, toggleLayer, showConnectedOnly, setShowConnectedOnly,
    connectedConnectors, selectedSchema, selectedTable, selectTable, erd, erdLoading, ensureErd,
  } = resolveVaultSidebarContext(v);

  // Tree-expand state. We control it manually because the schema's
  // children (table list) are loaded lazily on first expand.
  const [expandedKeys, setExpandedKeys] = useState([]);

  // Columns lookup keyed by `schema_name.table` — built from the lazy
  // ERD payload. Used to render expandable column children under each
  // table row in the Catalog tab.
  const columnsByTable = useMemo(() => {
    const map = new Map();
    for (const t of (erd?.tables || [])) {
      map.set(`${t.schema_name}.${t.table}`, t.columns || []);
    }
    return map;
  }, [erd]);

  // Build the schema rows from the lightweight `listVaultSchemas()`
  // result and (when search is empty) render schemas only — table
  // children are loaded lazily on expand. With a search query active
  // we fall back to whatever tables happen to be loaded already (cached
  // schemas + ERD if it's been fetched) so users can filter across
  // anything that's in memory without forcing a full warehouse scan.
  const filteredSchemas = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = layerSchemas.map((s) => ({
      schema_name: s.schema_name,
      connector: s.connector || s.schema_name,
      table_count: s.table_count || 0,
      layer: s.layer || 'legacy',
    }));
    if (showConnectedOnly) {
      rows = rows.filter((s) => connectedConnectors.has(s.connector));
    }
    if (q) {
      rows = rows.filter((s) => {
        if (s.connector.toLowerCase().includes(q)) {return true;}
        const cached = tablesBySchema[s.schema_name] || [];
        return cached.some((t) => t.table.toLowerCase().includes(q));
      });
    }
    return rows.sort((a, b) => a.connector.localeCompare(b.connector));
  }, [layerSchemas, search, tablesBySchema, showConnectedOnly, connectedConnectors]);

  // Tree key conventions:
  //   schema:   's::<connector>'
  //   table:    't::<connector>::<table>'
  //   column:   'c::<schema_name>.<table>::<column>'
  const treeData = useMemo(
    () => buildVaultTreeData(filteredSchemas, {
      tablesBySchema, tablesLoading, search, columnsByTable, erd, erdLoading, tab,
    }),
    [filteredSchemas, tablesBySchema, tablesLoading, search, columnsByTable, erd, erdLoading, tab],
  );

  // Auto-expand all schemas while a search query is active so matches
  // are visible without manual expand. With no search, only schemas
  // the user explicitly opened are expanded (stays in `expandedKeys`).
  const groupKeys = useMemo(
    () => Array.from(new Set(filteredSchemas.map((s) => `l::${s.layer || 'legacy'}`))),
    [filteredSchemas],
  );

  // Layer-group rows start expanded so schemas are visible without an
  // extra click; users can still collapse a group (onExpand persists it).
  useEffect(() => {
    setExpandedKeys((prev) => {
      const have = new Set(prev);
      let changed = false;
      for (const k of groupKeys) {if (!have.has(k)) { have.add(k); changed = true; }}
      return changed ? Array.from(have) : prev;
    });
  }, [groupKeys]);

  const effectiveExpandedKeys = useMemo(() => {
    if (search.trim()) {return [...groupKeys, ...filteredSchemas.map((s) => `s::${s.connector}`)];}
    return expandedKeys;
  }, [search, filteredSchemas, expandedKeys, groupKeys]);

  // `expandedKeys` is keyed by connector (`s::jira`) so it survives layer
  // switches, but `tablesBySchema` is keyed by the full layer-prefixed
  // schema name (`bronze__jira`). When the user switches layers while a
  // schema is open, the new layer's tables haven't been fetched yet and
  // `onExpand` won't refire (the node is still expanded). Fire the lazy
  // load for any expanded schema missing cached tables.
  useEffect(() => {
    const open = new Set(effectiveExpandedKeys);
    for (const s of filteredSchemas) {
      const needsLoad = open.has(`s::${s.connector}`)
        && !tablesBySchema[s.schema_name]
        && !tablesLoading[s.schema_name];
      if (needsLoad) {loadSchemaTables(s.schema_name);}
    }
  }, [filteredSchemas, effectiveExpandedKeys, tablesBySchema, tablesLoading, loadSchemaTables]);

  // Pre-load tables for any schema that has cached results matching the
  // search query (handled at filter time) — but also kick off a load
  // for schemas whose connector name matches the search, so the user
  // sees their tables once they expand.
  const onExpand = useCallback((keys, info) => {
    setExpandedKeys(keys);
    // When user just expanded a schema node, fire the lazy load.
    if (info?.expanded && info?.node?._schemaName) {
      loadSchemaTables(info.node._schemaName);
    }
    // When user expands a table node (Catalog tab), make sure the full
    // ERD (which carries column lists) is loaded.
    if (info?.expanded && info?.node?._tableId && !erd && !erdLoading) {
      ensureErd();
    }
  }, [loadSchemaTables, ensureErd, erd, erdLoading]);

  // Note: we used to ensure the full ERD on table select so the right
  // pane had column metadata immediately, but that pulled every table's
  // columns + FKs just to render one table. The Catalog tab now fetches
  // per-table schema via /vault/metadata?kind=columns on demand instead — only
  // expanding a table in the tree (for inline column children) forces
  // the full ERD (handled by onExpand above).

  const selectedKey = resolveSelectedTreeKey({ selectedSchema, selectedTable });

  const onSelect = (keys, info) => {
    const k = keys[0];
    if (!k || !k.startsWith('t::')) {return;}
    const [, schema, table] = k.split('::');
    // Pass the table's actual medallion layer — the connector+table key
    // alone is ambiguous when multiple layers are shown, and downstream
    // schema resolution needs the real layer to build `<layer>__<connector>`.
    selectTable(schema, table, info?.node?._table?.layer);
    if (onDoubleClickTable && info?.nativeEvent?.detail === 2) {
      onDoubleClickTable(info.node?._table);
    }
  };

  const onDoubleClick = (_, { node }) => {
    if (onDoubleClickTable && node?._table) {onDoubleClickTable(node._table);}
  };

  const layerOptions = buildLayerOptions(layerCounts);
  const schemaTreePanelKind = resolveSchemaTreePanelKind({ loading, treeDataLength: treeData.length });

  // Bail after all hooks have run — outside-of-provider safety net.
  if (!v) {return null;}

  return (
    <div
      className="vault-sidebar"
      style={{
        width,
        flex: `0 0 ${width}px`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid var(--theme-border, #f0f0f0)',
        background: 'var(--theme-cardBg, #fff)',
        overflow: 'hidden',
      }}
    >
      <div className="vault-sidebar-head">
        <div className="vault-sidebar-titlebar">
          <div className="vault-sidebar-title">
            <DatabaseOutlined className="vault-sidebar-title-icon" />
            <span>Schemas &amp; Tables</span>
          </div>
          <Space size={2}>
            <Tooltip title="Refresh">
              <Button
                size="small"
                type="text"
                icon={<ReloadOutlined />}
                onClick={refresh}
              />
            </Tooltip>
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              menu={{
                items: [
                  {
                    key: 'reset',
                    label: 'Reset data…',
                    icon: <DeleteOutlined />,
                    danger: true,
                    onClick: () => setResetOpen(true),
                  },
                ],
              }}
            >
              <Button size="small" type="text" icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        </div>
        <div className="vault-layer-chips" role="group" aria-label="Medallion layers">
          {layerOptions.map((opt) => {
            const checked = layers.includes(opt.value);
            const disabled = !opt.count && !checked;
            return (
              <button
                key={opt.value}
                type="button"
                aria-pressed={checked}
                disabled={disabled}
                onClick={() => !disabled && toggleLayer(opt.value)}
                className={[
                  'vault-layer-chip',
                  `vault-layer-chip-${opt.value}`,
                  checked ? 'is-checked' : '',
                  disabled ? 'is-disabled' : '',
                ].filter(Boolean).join(' ')}
                title={disabled ? `No tables in ${opt.label}` : `Toggle ${opt.label}`}
              >
                <span className="vault-layer-chip-label">{opt.label}</span>
                <span className="vault-layer-chip-count">{opt.count} {opt.count === 1 ? 'table' : 'tables'}</span>
              </button>
            );
          })}
        </div>
        <Checkbox
          className="vault-connected-only"
          checked={showConnectedOnly}
          onChange={(e) => setShowConnectedOnly(e.target.checked)}
          style={{ marginBottom: 8, fontSize: 12 }}
        >
          Display only connected apps
        </Checkbox>
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          placeholder={resolveSchemaSearchPlaceholder(tab)}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Tree */}
      <div className="vault-sidebar-tree" style={{ flex: 1, overflow: 'auto', padding: '8px 6px', minHeight: 0 }}>
        {schemaTreePanelKind === 'loading' && (
          <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
        )}
        {schemaTreePanelKind === 'empty' && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={resolveEmptySchemaMessage(layerSchemas.length)}
          />
        )}
        {schemaTreePanelKind === 'ready' && (
          <Tree
            blockNode
            className="vault-sidebar-tree-cmp"
            treeData={treeData}
            expandedKeys={effectiveExpandedKeys}
            onExpand={onExpand}
            selectable
            selectedKeys={selectedKey}
            onSelect={onSelect}
            onDoubleClick={resolveTreeDoubleClickHandler({ tab, onDoubleClick })}
          />
        )}
      </div>

      <ResetVaultDialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        onAfterReset={() => { setResetOpen(false); refresh(); }}
      />
    </div>
  );
}
